// InstaPost.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ReactComponent as ReturnArrow } from "../../assets/icons/returnArrow.svg";
import { ReactComponent as Download } from "../../assets/icons/download.svg";
import { ReactComponent as CopyToClipboard } from "../../assets/icons/copyToClipboard.svg";
import { ReactComponent as ExpandSvg } from "../../assets/icons/expandIconDVF.svg";
import { ReactComponent as HeartIcon } from "../../assets/icons/heart.svg";
import { FeedItem, SelectedPost } from "../../interface/DesignerInterface";
import {
  copyImageToClipboard,
  getDisplaySource,
  getImageLink,
} from "../../utils/commonUtils";
import Modal from "../Common/Modal";
import DownloadImage from "./DownloadImage";
import ExpandView from "../../components/DesignerPage/ExpandView";
import SaveToBoard from "./SaveToBoard";
import { Board } from "../../interface/BoardsInterface";

interface InstaPostProps {
  item: FeedItem;
  trendsOpen: boolean;
  privateBoards: Board[];
  setPrivateBoards: React.Dispatch<React.SetStateAction<Board[]>>;
  publicBoards: Board[];
  setPublicBoards: React.Dispatch<React.SetStateAction<Board[]>>;
  userId: string;
  analysisResultSuccess?: boolean;
  className?: string;
}

const InstaPost: React.FC<InstaPostProps> = ({
  item,
  trendsOpen,
  privateBoards,
  setPrivateBoards,
  publicBoards,
  setPublicBoards,
  userId,
  analysisResultSuccess,
  className,
}) => {
  const [isDownloadImageOpen, setDownloadImageOpen] = useState(false);
  const [isExpandViewOpen, setExpandViewOpen] = useState(false);
  const [saveToBoardImage, setSaveToBoardImage] = useState<string>("");
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const imageUrl = useMemo(
    () => getImageLink(item.image_link),
    [item.image_link]
  );

  const imageHeight = trendsOpen ? 190 : 300;

  useEffect(() => {
    setIsImageLoaded(false);
  }, [imageUrl]);

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setIsImageLoaded(true);
    }
  }, [imageUrl]);

  const selectedPost = {
    imageUrl,
    brandName: item.brand.brandName,
    description: item.description,
    source: item.handle,
    scraping_source_id: item.scraping_source_id,
    postId: item.postId,
    permalink: item.permalink,
    liveSince: item.postDate,
  };

  const openExpandView = () => {
    setExpandViewOpen(true);
  };

  const closeExpandView = () => {
    setExpandViewOpen(false);
  };
  const openDownloadImage = () => {
    setDownloadImageOpen(true);
  };

  const closeDownloadImage = () => setDownloadImageOpen(false);
  const handleCopyImageToClipboard = async (imageUrl: string) => {
    await copyImageToClipboard(
      imageUrl,
      getDisplaySource(item.scraping_source_id, item.handle)
    );
  };

  const [isSaveToBoardOpen, setSaveToBoardOpen] = useState(false);
  const closeSaveToBoard = () => setSaveToBoardOpen(false);

  const formatDate = (date: Date) => {
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  };

  const openSaveToBoard = (imageUrl: string) => {
    setSaveToBoardImage(imageUrl);
    setSaveToBoardOpen(true);
  };
  const handleOpenSaveToBoard = (imageUrl: string, post: FeedItem) => {
    setSaveToBoardImage(imageUrl);
    setSaveToBoardOpen(true);
  };

  if (!item.image_link || item.image_link === "N/A") {
    return null;
  }
  return (
    <div
      key={item.postId}
      className={`${trendsOpen ? "w-[158px]" : "w-[240px]"} ${trendsOpen ? "h-[260px]" : "h-[380px]"} p-0 pb-3 gap-2 border border-light rounded-2xl relative ${className && className}`}
    >
      <div
        className={`relative w-full ${trendsOpen ? "h-[190px]" : "h-[300px]"} hover:transition-all duration-100`}
      >
        <div
          className={`relative w-full ${trendsOpen ? "h-[190px]" : "h-[300px]"} overflow-hidden rounded-t-2xl`}
        >
          {!isImageLoaded && (
            <Skeleton
              height={imageHeight}
              width="100%"
              borderRadius={0}
              className="absolute inset-0 block leading-none"
            />
          )}
          <img
            ref={imgRef}
            src={imageUrl}
            alt={`Image ${item.postId}`}
            className={`w-full h-full object-cover rounded-t-2xl transition-opacity duration-200 ${
              isImageLoaded ? "opacity-100" : "opacity-0"
            }`}
            loading="lazy"
            onLoad={() => setIsImageLoaded(true)}
            onError={() => setIsImageLoaded(true)}
          />
        </div>
        <div
          className={`absolute top-0 right-0 bg-translucent text-white bg-gray81/50 rounded-bl-lg rounded-tr-2xl ${trendsOpen ? "px-2 py-1" : "px-1 py-0.5"} ${trendsOpen ? "text-[10px] max-w-[140px]" : "text-xs max-w-[220px]"} truncate  overflow-hidden whitespace-nowrap`}
        >
          {getDisplaySource(item?.scraping_source_id, item.handle)}
        </div>
        <div className="absolute bottom-2 right-2 flex space-x-1">
          <div
            className="bg-white flex w-5 h-5 items-center justify-center rounded-full cursor-pointer"
            onClick={() => window.open(`${item?.permalink}`, "_blank")}
          >
            <ReturnArrow />
          </div>
          <div
            className="bg-white flex w-5 h-5 items-center justify-center rounded-full cursor-pointer"
            onClick={() => handleOpenSaveToBoard(imageUrl, item)}
          >
            <HeartIcon />
          </div>
          <div
            className="bg-white flex w-5 h-5 shadow-2xl shadow-gray-900 items-center justify-center rounded-full cursor-pointer"
            onClick={async () => await openDownloadImage()}
          >
            <Download />
          </div>
          <div
            className="bg-white flex w-5 h-5 items-center justify-center rounded-full cursor-pointer"
            onClick={() => handleCopyImageToClipboard(imageUrl)}
          >
            <CopyToClipboard />
          </div>
        </div>
      </div>
      <div className={`p-2 space-y-0.5 ${trendsOpen ? "leading-3" : ""}`}>
        <div
          className={`${trendsOpen ? "text-[10px]" : "text-xs"} mt-1 text-primary font-medium truncate`}
        >
          Brand: {item.brand.brandName}
        </div>
        <div
          className={`${trendsOpen ? "text-[10px]" : "text-xs"} text-primary`}
        >
          Date: {formatDate(new Date(item.postDate))}
        </div>
        <div
          className={`flex justify-between items-center ${trendsOpen ? "text-[10px]" : "text-xs"} text-tertiary`}
        >
          <span className={`w-[95%] truncate`}>{item.description}</span>
        </div>
        <button
          className={`absolute bottom-1.5 right-2 `}
          onClick={openExpandView}
        >
          <ExpandSvg className="w-3 h-3" />
        </button>
      </div>
      <Modal
        isOpen={isDownloadImageOpen}
        onClose={closeDownloadImage}
        title="Download Image"
      >
        <DownloadImage
          imageUrl={imageUrl}
          source={item.handle}
          brandName={item.brand.brandName}
          scrapingSourceId={item.scraping_source_id}
        />
      </Modal>
      <Modal
        isOpen={isExpandViewOpen}
        onClose={closeExpandView}
        title="Expanded View"
      >
        {selectedPost && (
          <ExpandView
            imageUrl={selectedPost.imageUrl}
            brandName={selectedPost.brandName}
            description={selectedPost.description}
            source={selectedPost.source}
            openSaveToBoard={openSaveToBoard}
            openDownloadImage={openDownloadImage}
            postId={selectedPost.postId}
            permalink={selectedPost.permalink}
            liveSince={selectedPost.liveSince}
            scraping_source_id={selectedPost.scraping_source_id}
          />
        )}
      </Modal>
      <Modal
        isOpen={isSaveToBoardOpen}
        onClose={closeSaveToBoard}
        title="Save to Board"
      >
        {saveToBoardImage && (
          <SaveToBoard
            imageUrl={saveToBoardImage}
            selectedPost={selectedPost}
            publicBoards={publicBoards}
            setPublicBoards={setPublicBoards}
            privateBoards={privateBoards}
            setPrivateBoards={setPrivateBoards}
            onClose={closeSaveToBoard}
            userId={userId}
            analysisResultSuccess={analysisResultSuccess}
          />
        )}
      </Modal>
    </div>
  );
};

export default InstaPost;
